// @spec SHL-STR-040
import { useCallback, useDebugValue, useSyncExternalStore } from 'react';

/**
 * Return type for the useOnlineStatus hook.
 */
export interface UseOnlineStatusReturn {
  /** Whether the browser is currently online */
  isOnline: boolean;
  /** Force a connection check by pinging a health endpoint */
  checkConnection: () => Promise<boolean>;
}

/**
 * Subscribe to online/offline events.
 */
function subscribe(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

/**
 * Get the current online status from the browser.
 */
function getSnapshot(): boolean {
  return navigator.onLine;
}

/**
 * Server-side rendering fallback - assume online.
 */
function getServerSnapshot(): boolean {
  return true;
}

/**
 * Hook for detecting browser online/offline status.
 *
 * Uses useSyncExternalStore for proper hydration and server-side rendering.
 * Subscribes to the browser's online/offline events and provides a method
 * to manually check connectivity.
 *
 * @returns Object with isOnline status and checkConnection method
 *
 * @example
 * function NetworkStatus() {
 *   const { isOnline, checkConnection } = useOnlineStatus();
 *
 *   return (
 *     <div>
 *       <span>{isOnline ? 'Online' : 'Offline'}</span>
 *       <button onClick={checkConnection}>Check</button>
 *     </div>
 *   );
 * }
 */
export function useOnlineStatus(): UseOnlineStatusReturn {
  const isOnline = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useDebugValue(isOnline ? 'Online' : 'Offline');

  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      // Try to fetch a small endpoint to verify actual connectivity
      const response = await fetch('/api/v1/health', {
        method: 'HEAD',
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  return { isOnline, checkConnection };
}
